// TypeScript React
import React, { useEffect, useRef, useState } from 'react';
import { Box, Fab, IconButton, Paper, Slide, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import CloseIcon from '@mui/icons-material/Close';
import edisonIcon from 'assets/edison_icon1.png';
import EdisonDragIndicatorIcon from './EdisonDragIndicatorIcon';
import { createPortal } from 'react-dom';
import { useTheme } from '@mui/material/styles';
import { useEdisonDock } from 'hooks/EdisonChatDock';
import EdisonChat from './EdisonChat';
import { AiAgentTypes } from '../../../types/ai.types';

const EdisonChatDock: React.FunctionComponent = () => {
    const { isOpen, setOpen, bottomOffset, rightOffset, isHidden, showResearchTab, entityContext } = useEdisonDock();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Tab state
    const [currentTab, setCurrentTab] = useState(0);

    // Resizing state
    const [panelWidth, setPanelWidth] = useState(700);
    const [panelHeight, setPanelHeight] = useState(Math.floor(window.innerHeight * 0.8));
    const [isResizing, setIsResizing] = useState(false);
    const resizingRef = useRef<{
        direction: string;
        startX: number;
        startY: number;
        startWidth: number;
        startHeight: number;
    } | null>(null);

    // Ensure we render above most UI
    const zIndex = (theme.zIndex.tooltip || 1100) + 10;

    const displayWidth = isMobile ? '100vw' : panelWidth;
    const displayHeight = isMobile ? '100vh' : panelHeight;

    const bottomOffsetBase = '5%';
    const rightOffsetBase = '3%';
    const panelRight = isMobile ? 0 : rightOffsetBase;
    const panelBottom = isMobile ? 0 : bottomOffsetBase;

    // Handle resize start
    const handleResizeStart = (direction: string) => (e: React.MouseEvent) => {
        if (isMobile) return;
        e.preventDefault();
        resizingRef.current = {
            direction,
            startX: e.clientX,
            startY: e.clientY,
            startWidth: panelWidth,
            startHeight: panelHeight,
        };
        setIsResizing(true);
    };

    // Handle resize move
    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (!resizingRef.current) return;

            const { direction, startX, startY, startWidth, startHeight } = resizingRef.current;

            let newWidth = startWidth;
            let newHeight = startHeight;

            // Calculate deltas based on direction
            // For right-anchored panel: moving left increases width, moving right decreases width
            // For bottom-anchored panel: moving up increases height, moving down decreases height
            const deltaX = startX - e.clientX; // Positive when moving left
            const deltaY = startY - e.clientY; // Positive when moving up

            // Handle horizontal resizing
            if (direction.includes('left') || direction.includes('right')) {
                newWidth = startWidth + deltaX;
            }

            // Handle vertical resizing
            if (direction.includes('top') || direction.includes('bottom')) {
                newHeight = startHeight + deltaY;
            }

            // Apply constraints (minimum and maximum based on viewport)
            // Calculate max dimensions considering the offset from edges
            const rightOffsetPx = window.innerWidth * 0.03; // 3% offset
            const bottomOffsetPx = window.innerHeight * 0.05; // 5% offset
            const maxWidth = window.innerWidth - rightOffsetPx;
            const maxHeight = window.innerHeight - bottomOffsetPx;

            newWidth = Math.max(300, Math.min(maxWidth, newWidth));
            newHeight = Math.max(300, Math.min(maxHeight, newHeight));

            setPanelWidth(newWidth);
            setPanelHeight(newHeight);
        };

        const handleMouseUp = () => {
            resizingRef.current = null;
            setIsResizing(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    return createPortal(
        <>
            {/* Launcher Button */}
            {!isOpen && !isHidden && (
                <Fab
                    color="secondary"
                    aria-label="Open Edison"
                    onClick={() => setOpen(true)}
                    sx={{
                        position: 'fixed',
                        bottom: `calc(${bottomOffsetBase} + ${bottomOffset}px)`,
                        right: `calc(${rightOffsetBase} + ${rightOffset}px)`,
                        boxShadow: 6,
                        '&:hover': {
                            backgroundColor: 'secondary.main',
                            transform: 'scale(1.2)', //slight pop effect
                        },
                    }}
                    size={isMobile ? 'medium' : 'large'}
                >
                    <img
                        src={edisonIcon}
                        alt="Edison AI Assistant"
                        style={{
                            width: 70,
                            height: 70,
                            objectFit: 'contain',
                        }}
                    />
                </Fab>
            )}

            {/* Slide-out Panel */}
            <Box
                role="dialog"
                aria-label="Agent Chat"
                sx={{
                    zIndex: zIndex,
                    position: 'fixed',
                    right: panelRight,
                    bottom: panelBottom,
                    width: displayWidth,
                    height: displayHeight,
                    pointerEvents: isOpen ? 'auto' : 'none',
                }}
            >
                <Slide direction="up" in={isOpen}>
                    <Paper
                        elevation={8}
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: isMobile ? 0 : 2,
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        {/* Resize Handles - only show on desktop */}
                        {!isMobile && (
                            <>
                                {/* Corner Handles (top-left now includes visual drag indicator) */}
                                <Box
                                    onMouseDown={handleResizeStart('top-left')}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: 20,
                                        height: 20,
                                        cursor: 'nwse-resize',
                                        zIndex: 11,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'flex-start',
                                        p: 0,
                                        '&:hover': {
                                            bgcolor: 'primary.main',
                                            opacity: 0.18,
                                        },
                                        '&:hover .MuiSvgIcon-root': {
                                            color: 'primary.main',
                                            opacity: 0.9,
                                        },
                                    }}
                                >
                                    <EdisonDragIndicatorIcon
                                        aria-hidden
                                        sx={{
                                            fontSize: 14,
                                            color: 'text.disabled',
                                            opacity: 0.7,
                                            pointerEvents: 'none',
                                            mt: 0.2,
                                            ml: 0.2,
                                        }}
                                    />
                                </Box>
                                <Box
                                    onMouseDown={handleResizeStart('top-right')}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: 16,
                                        height: 16,
                                        cursor: 'nesw-resize',
                                        zIndex: 10,
                                        '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                                    }}
                                />
                                <Box
                                    onMouseDown={handleResizeStart('bottom-left')}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        width: 16,
                                        height: 16,
                                        cursor: 'nesw-resize',
                                        zIndex: 10,
                                        '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                                    }}
                                />
                                <Box
                                    onMouseDown={handleResizeStart('bottom-right')}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        width: 16,
                                        height: 16,
                                        cursor: 'nwse-resize',
                                        zIndex: 10,
                                        '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                                    }}
                                />

                                {/* Edge Handles */}
                                <Box
                                    onMouseDown={handleResizeStart('top')}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 16,
                                        right: 16,
                                        height: 4,
                                        cursor: 'ns-resize',
                                        zIndex: 9,
                                        '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                                    }}
                                />
                                <Box
                                    onMouseDown={handleResizeStart('bottom')}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 16,
                                        right: 16,
                                        height: 4,
                                        cursor: 'ns-resize',
                                        zIndex: 9,
                                        '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                                    }}
                                />
                                <Box
                                    onMouseDown={handleResizeStart('left')}
                                    sx={{
                                        position: 'absolute',
                                        top: 16,
                                        bottom: 16,
                                        left: 0,
                                        width: 4,
                                        cursor: 'ew-resize',
                                        zIndex: 9,
                                        '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                                    }}
                                />
                                <Box
                                    onMouseDown={handleResizeStart('right')}
                                    sx={{
                                        position: 'absolute',
                                        top: 16,
                                        bottom: 16,
                                        right: 0,
                                        width: 4,
                                        cursor: 'ew-resize',
                                        zIndex: 9,
                                        '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                                    }}
                                />
                            </>
                        )}

                        {/* Header */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                borderBottom: 1,
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1.5,
                                    py: 1,
                                }}
                            >
                                <img
                                    src={edisonIcon}
                                    alt="Edison AI Assistant"
                                    style={{
                                        width: 48,
                                        height: 48,
                                        objectFit: 'contain',
                                        marginRight: 1,
                                    }}
                                />
                                <Typography variant="h3" sx={{ flex: 1, alignItems: 'center' }}>
                                    Edison AI
                                </Typography>

                                <IconButton aria-label="Close" onClick={() => setOpen(false)} size="small">
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <Tabs
                                value={currentTab}
                                onChange={(e, newValue) => setCurrentTab(newValue)}
                                sx={{ minHeight: 48 }}
                            >
                                {showResearchTab && <Tab label="Research Assistant" />}
                                <Tab label="Data Assistant" />
                            </Tabs>
                        </Box>

                        {/* Chat Body */}
                        <Box sx={{ flex: 1, minHeight: 0, bgcolor: 'background.default', display: 'flex' }}>
                            <Box
                                key="research-chat"
                                sx={{
                                    display: showResearchTab ? (currentTab === 0 ? 'flex' : 'none') : 'none',
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                <EdisonChat
                                    isOpen={isOpen}
                                    agentType={AiAgentTypes.RESEARCH}
                                    contextType={entityContext?.contextType}
                                    contextId={entityContext?.contextId}
                                />
                            </Box>
                            <Box
                                key="edison-chat"
                                sx={{
                                    display: showResearchTab ? (currentTab === 1 ? 'flex' : 'none') : 'flex',
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                <EdisonChat isOpen={isOpen} agentType={AiAgentTypes.EDISON} />
                            </Box>
                        </Box>
                    </Paper>
                </Slide>
            </Box>
        </>,
        document.body
    );
};

export default EdisonChatDock;
